/**
 * Graph compound tool — find, suggest.
 * Consolidates find-related and suggest-links into one read-only tool.
 * find: traverses relations from a specific entry (graph walk). NOT full-text search — use entries action=search.
 * suggest: recommends new relations (link discovery).
 */

import { z } from "zod/v3";
import { kernelQuery } from "../client.js";
import { success, notFoundResult, failureResult } from "../envelope.js";

const GRAPH_ACTIONS = ["find", "suggest"] as const;

export const graphSchema = z.object({
  action: z.enum(GRAPH_ACTIONS).describe(
    "'find': traverse relations from an entry (graph walk). 'suggest': discover potential connections for an entry.",
  ),
  entryId: z.string().max(200).describe("Entry ID, e.g. '<PREFIX>-<n>'"),
  direction: z.enum(["incoming", "outgoing", "both"]).default("both").optional()
    .describe("For find: 'incoming' = what references this, 'outgoing' = what this references"),
  limit: z.number().min(1).max(20).default(10).optional()
    .describe("For suggest: max suggestions to return"),
  depth: z.number().min(1).max(3).default(2).optional()
    .describe("For suggest: graph traversal depth"),
});

export const graphFindOutputSchema = z.object({
  entryId: z.string(),
  relations: z.array(z.object({
    entryId: z.string().optional(),
    name: z.string(),
    type: z.string(),
    direction: z.enum(["outgoing", "incoming"]),
  })),
  total: z.number(),
});

export const graphSuggestOutputSchema = z.object({
  entryId: z.string(),
  suggestions: z.array(z.object({
    targetEntryId: z.string().optional(),
    targetName: z.string(),
    relationType: z.string(),
    direction: z.string(),
    confidence: z.number(),
    reason: z.string(),
  })),
  total: z.number(),
});

// WP-484 S3 (clean break, TEN-2157): the standalone `graph` tool retired
// here. handleFind / handleSuggest stay live — relations.ts (the `relations`
// compound, action=find / action=suggest) is their only remaining caller.

export async function handleFind(entryId: string, direction: "incoming" | "outgoing" | "both") {
  const relations = await kernelQuery<Array<{ fromId: string; toId: string; type: string }>>("chain.listEntryRelations", { entryId });

  if (relations.length === 0) {
    return {
      content: [{ type: "text" as const, text: `No relations found for \`${entryId}\`. Use relations action=create to create connections.` }],
      structuredContent: success(
        `No relations found for '${entryId}'.`,
        { entryId, relations: [], nodes: [], edges: [], total: 0 },
        [
          { tool: "relations", description: "Create a relation", parameters: { action: "create", from: entryId } },
          { tool: "relations", description: "Get suggestions", parameters: { action: "suggest", entryId } },
        ],
      ),
    };
  }

  const sourceEntry = await kernelQuery<{ _id: string; name: string } | null>("chain.getEntry", { entryId });
  if (!sourceEntry) {
    return notFoundResult(entryId, `Entry '${entryId}' not found. Try entries action=search to find the right ID.`);
  }
  const sourceInternalId = sourceEntry._id;

  const MAX_RELATIONS = 25;
  const truncated = relations.length > MAX_RELATIONS;
  const capped = relations.slice(0, MAX_RELATIONS);

  const otherIds = new Set<string>();
  for (const r of capped) {
    const otherId = r.fromId === sourceInternalId ? r.toId : r.fromId;
    otherIds.add(otherId);
  }

  const otherEntries = new Map<string, { entryId?: string; name: string; collectionId: string }>();
  for (const id of otherIds) {
    const entry = await kernelQuery<{ _id: string; entryId?: string; name: string; collectionId: string } | null>("chain.getEntry", { id });
    if (entry) {
      otherEntries.set(entry._id, { entryId: entry.entryId, name: entry.name, collectionId: entry.collectionId });
    }
  }

  const collections = await kernelQuery<Array<{ _id: string; slug: string }>>("chain.listCollections");
  const collMap = new Map<string, string>();
  for (const c of collections) collMap.set(c._id, c.slug);

  const lines: string[] = [`## Find Result`, "", `# Relations for ${entryId}: ${sourceEntry.name}`, ""];

  const enriched = capped.map((r) => {
    const isOutgoing = r.fromId === sourceInternalId;
    const otherId = isOutgoing ? r.toId : r.fromId;
    const other = otherEntries.get(otherId);
    const otherLabel = other?.entryId ? `${other.entryId}: ${other.name}` : (other?.name ?? "(deleted)");
    const colSlug = other ? (collMap.get(other.collectionId) ?? "unknown") : "unknown";
    return { isOutgoing, type: r.type, otherLabel, colSlug, otherEntryId: other?.entryId, otherName: other?.name ?? "(deleted)" };
  });

  const outgoing = enriched.filter((r) => r.isOutgoing);
  const incoming = enriched.filter((r) => !r.isOutgoing);

  if ((direction === "outgoing" || direction === "both") && outgoing.length > 0) {
    lines.push(`## Outgoing (${outgoing.length})`);
    for (const r of outgoing) {
      lines.push(`- → **${r.type}** ${r.otherLabel} [${r.colSlug}]`);
    }
    lines.push("");
  }

  if ((direction === "incoming" || direction === "both") && incoming.length > 0) {
    lines.push(`## Incoming (${incoming.length})`);
    for (const r of incoming) {
      lines.push(`- ← **${r.type}** ${r.otherLabel} [${r.colSlug}]`);
    }
    lines.push("");
  }

  const shown = direction === "outgoing" ? outgoing : direction === "incoming" ? incoming : enriched;
  if (shown.length === 0) {
    lines.push(`No ${direction} relations found for \`${entryId}\`.`);
  }

  if (truncated) {
    lines.push(`_Showing first ${MAX_RELATIONS} of ${relations.length} relations. Use entries action=get for the full picture._`);
  }

  const structuredRelations = shown.map((r) => ({
    entryId: r.otherEntryId,
    name: r.otherName,
    type: r.type,
    direction: (r.isOutgoing ? "outgoing" : "incoming") as "outgoing" | "incoming",
  }));

  const nodes = [
    { id: entryId, name: sourceEntry.name, collectionName: "" },
    ...shown.map((r) => ({
      id: r.otherEntryId ?? String(Math.random()),
      name: r.otherName,
      collectionName: r.colSlug,
    })),
  ];
  const edges = shown
    .filter((r) => r.otherEntryId)
    .map((r) => ({
      source: entryId,
      target: r.otherEntryId!,
      type: r.type,
    }));

  return {
    content: [{ type: "text" as const, text: lines.join("\n") }],
    structuredContent: success(
      `Found ${relations.length} relations for ${entryId}.`,
      { entryId, relations: structuredRelations, nodes, edges, total: relations.length },
      [{ tool: "entries", description: "View entry details", parameters: { action: "get", entryId } }],
    ),
  };
}

export async function handleSuggest(entryId: string, limit: number, depth: number) {
  const result = await kernelQuery<{
    resolvedEntry?: { entryId?: string; name: string };
    suggestions?: Array<{
      entryId?: string;
      name: string;
      collectionSlug: string;
      score: number;
      recommendedRelationType: string;
      reasoning: string;
      preview?: string;
      graphDistance: number;
    }>;
  }>("chain.graphSuggestLinks", {
    entryId,
    maxHops: depth,
    limit,
  });

  if (!result?.suggestions || result.suggestions.length === 0) {
    return failureResult(
      `No suggestions found for '${entryId}' — it may already be well-connected, or no similar entries exist.`,
      "NOT_FOUND",
      `No suggestions found for '${entryId}'.`,
      "Entry may already be well-connected.",
      [{ tool: "relations", description: "Check existing relations", parameters: { action: "find", entryId } }],
    );
  }

  const resolved = result.resolvedEntry;
  const resolvedLabel = resolved
    ? `\`${resolved.entryId ?? resolved.name}\` (${resolved.name})`
    : `\`${entryId}\``;

  const suggestions = result.suggestions;
  const graphCount = suggestions.filter((s) => s.graphDistance > 0).length;
  const textCount = suggestions.filter((s) => s.graphDistance === -1).length;
  const sourceId = resolved?.entryId ?? entryId;

  const lines = [
    `## Suggest Result`,
    "",
    `# Link Suggestions for ${resolvedLabel}`,
    `_${suggestions.length} potential connections (${graphCount} via graph, ${textCount} via text similarity)._`,
    "",
  ];

  const top3 = suggestions.slice(0, 3);
  lines.push("## Quick Wins (top 3)");
  const batchArgs: string[] = [];
  for (const s of top3) {
    const tid = s.entryId ?? "(no ID)";
    const relType = s.recommendedRelationType || "related_to";
    lines.push(`- **${tid}**: ${s.name} [${s.collectionSlug}] — ${s.score}/100 → \`${relType}\``);
    if (s.entryId) batchArgs.push(`{from:"${sourceId}",to:"${tid}",type:"${relType}"}`);
  }
  lines.push("");
  if (batchArgs.length > 0) {
    lines.push(`**Link all 3:** \`relations action=batch-create relations=[${batchArgs.join(",")}]\``);
    lines.push("");
  }

  if (suggestions.length > 3) {
    lines.push("## All Suggestions");
    for (let i = 0; i < suggestions.length; i++) {
      const s = suggestions[i];
      const scoreBar = "█".repeat(Math.round(s.score / 10)) + "░".repeat(10 - Math.round(s.score / 10));
      const hopLabel = s.graphDistance > 0 ? `${s.graphDistance}-hop` : "text";
      const recType = s.recommendedRelationType !== "related_to" ? ` → \`${s.recommendedRelationType}\`` : "";
      lines.push(`${i + 1}. **${s.entryId ?? "(no ID)"}**: ${s.name} [${s.collectionSlug}] (${hopLabel})`);
      lines.push(`   ${scoreBar} ${s.score}/100${recType}`);
      if (s.preview) lines.push(`   ${s.preview}`);
      lines.push(`   _${s.reasoning}_`);
    }
  }

  lines.push("");
  lines.push(`**To link one:** \`relations action=create from="${sourceId}" to="{target_id}" type="{type}"\``);
  lines.push(`**To dismiss:** \`relations action=dismiss from="${sourceId}" to="{target_id}" type="{type}"\``);

  const structuredSuggestions = suggestions.map((s) => ({
    targetEntryId: s.entryId,
    targetName: s.name,
    relationType: s.recommendedRelationType || "related_to",
    direction: "outgoing" as const,
    confidence: s.score,
    reason: s.reasoning,
  }));

  const nodes = [
    { id: sourceId, name: resolved?.name ?? entryId, collectionName: "" },
    ...suggestions.map((s) => ({
      id: s.entryId ?? String(Math.random()),
      name: s.name,
      collectionName: s.collectionSlug,
    })),
  ];
  const edges = suggestions
    .filter((s) => s.entryId)
    .map((s) => ({
      source: sourceId,
      target: s.entryId!,
      type: s.recommendedRelationType || "suggested",
    }));

  return {
    content: [{ type: "text" as const, text: lines.join("\n") }],
    structuredContent: success(
      `Found ${suggestions.length} link suggestions for ${resolvedLabel}.`,
      { entryId: sourceId, suggestions: structuredSuggestions, nodes, edges, total: suggestions.length },
      [{ tool: "relations", description: "Create links", parameters: { action: "batch-create" } }],
    ),
  };
}
